import { FolderOpenIcon, Trash2Icon } from "lucide-react";

import { useI18n } from "~/i18n";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";

/**
 * Confirm deleting a folder, with the two outcomes spelled out as separate choices:
 *  - Keep the files (safe, default) — the folder goes, its files return to Unfiled.
 *  - Delete the files too (destructive) — folder and contents are permanently removed.
 * An empty folder collapses to a single destructive confirm (no files to decide about).
 */
export function VaultDeleteFolderDialog({
  open,
  folderName,
  fileCount,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  folderName: string;
  fileCount: number;
  onOpenChange: (open: boolean) => void;
  /** `deleteContents` true = also delete the files inside; false = keep them (unfile). */
  onConfirm: (deleteContents: boolean) => void;
}) {
  const { t } = useI18n();
  const hasFiles = fileCount > 0;

  const confirm = (deleteContents: boolean) => {
    onConfirm(deleteContents);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>{t("vault.deleteFolderTitle", { name: folderName })}</DialogTitle>
          <DialogDescription>
            {hasFiles
              ? t("vault.deleteFolderBody", { count: fileCount })
              : t("vault.deleteFolderBodyEmpty")}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          {hasFiles ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => confirm(false)}
                className="flex w-full items-start gap-3 rounded-xl border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40"
              >
                <FolderOpenIcon
                  className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="space-y-0.5">
                  <span className="block text-sm font-semibold text-foreground">
                    {t("vault.deleteFolderKeepFiles")}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t("vault.deleteFolderKeepFilesHint")}
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => confirm(true)}
                className="flex w-full items-start gap-3 rounded-xl border border-destructive/30 bg-background p-3 text-left transition-colors hover:border-destructive hover:bg-destructive/5"
              >
                <Trash2Icon className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
                <span className="space-y-0.5">
                  <span className="block text-sm font-semibold text-destructive-foreground">
                    {t("vault.deleteFolderWithFiles")}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t("vault.deleteFolderWithFilesHint")}
                  </span>
                </span>
              </button>
            </div>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost-muted" onClick={() => onOpenChange(false)}>
              {t("vault.cancel")}
            </Button>
            {hasFiles ? null : (
              <Button type="button" variant="destructive" onClick={() => confirm(false)}>
                {t("vault.deleteFolder")}
              </Button>
            )}
          </div>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
